import { useEffect, useState } from 'react'
import { Box, Flex, Text, VStack } from '@chakra-ui/react'
import { Modal } from '@minetrace/ui'
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps'
import { LuUser as User, LuMapPin as MapPin, LuClock as Clock, LuInfo as Info } from 'react-icons/lu'
import { shipmentsService } from '../../api/services/shipments.service'
import type { ShipmentEventResponse } from '../../api/models/response/shipment'
import { getShipmentState } from '../../lib/shipmentStates'
import { parseUtcDate } from '../../lib/dates'
import { themeColors } from '../../theme'
import { KMap, FitBounds } from '../../lib/googleMaps'
import { createStateIcon } from './shipmentMapIcons'

type LL = google.maps.LatLngLiteral

interface Props {
  shipmentId: string | null
  batchCode?: string
  onClose: () => void
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '-'
  const d = parseUtcDate(iso)
  return (
    d.toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('es-PE', { hour: '2-digit', minute: '2-digit' })
  )
}

export function ShipmentEventsModal({ shipmentId, batchCode, onClose }: Props) {
  const [events, setEvents] = useState<ShipmentEventResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [hovered, setHovered] = useState<number | null>(null)
  const colors = themeColors

  useEffect(() => {
    if (!shipmentId) {
      setEvents([])
      return
    }
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const data = await shipmentsService.getEvents(shipmentId)
        if (!cancelled) setEvents(data)
      } catch {
        if (!cancelled) setError(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [shipmentId])

  const mapPoints: LL[] = events
    .filter(e => e.latitude != null && e.longitude != null)
    .map(e => ({ lat: e.latitude as number, lng: e.longitude as number }))

  return (
    <Modal
      open={shipmentId != null}
      onClose={onClose}
      title="Eventos del traslado"
      subtitle={batchCode ? `Lote ${batchCode}` : undefined}
      size="xl"
    >
      {loading && (
        <Text color={colors.textMuted} fontSize="sm" textAlign="center" py={8}>
          Cargando eventos...
        </Text>
      )}

      {error && (
        <Text color="#c8442f" fontSize="sm" textAlign="center" py={8}>
          Error al cargar los eventos
        </Text>
      )}

      {!loading && !error && events.length === 0 && (
        <Text color={colors.textMuted} fontSize="sm" textAlign="center" py={8}>
          No se registraron cambios de estado para este traslado.
        </Text>
      )}

      {!loading && !error && events.length > 0 && (
        <Flex gap={5} direction={{ base: 'column', md: 'row' }} alignItems="stretch">
          {/* Timeline */}
          <Box flex={1} minW={0}>
            <Text fontSize="xs" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.1em" mb={3}>
              Linea de tiempo
            </Text>
            <VStack align="stretch" gap={0} position="relative">
              {events.map((e, idx) => {
                const def = getShipmentState(e.shipmentState)
                const isLast = idx === events.length - 1
                return (
                  <Flex key={e.id} gap={3} alignItems="flex-start">
                    {/* Rail */}
                    <Flex direction="column" alignItems="center" flexShrink={0}>
                      <Box
                        w="14px" h="14px" borderRadius="full" bg={def.color}
                        border="3px solid white" boxShadow={`0 0 0 2px ${def.color}55`}
                      />
                      {!isLast && <Box w="2px" flex={1} minH="40px" bg={colors.border} my={1} />}
                    </Flex>

                    {/* Content */}
                    <Box flex={1} pb={isLast ? 0 : 5}>
                      <Text fontSize="sm" fontWeight="700" color={def.color}>
                        {def.label}
                      </Text>

                      <Flex alignItems="center" gap={1} mt={1}>
                        <Clock size={12} color={colors.textMuted} />
                        <Text fontSize="xs" color={colors.textMuted}>{formatDateTime(e.changedAt)}</Text>
                      </Flex>

                      <Flex alignItems="center" gap={1} mt={0.5}>
                        <User size={12} color={colors.textMuted} />
                        <Text fontSize="xs" color={colors.textMuted}>
                          {e.changedByUserName ?? 'Usuario desconocido'}
                        </Text>
                      </Flex>

                      {(e.latitude != null && e.longitude != null) && (
                        <Flex alignItems="center" gap={1} mt={0.5}>
                          <MapPin size={12} color={colors.textMuted} />
                          <Text fontSize="xs" color={colors.textMuted} fontFamily="monospace">
                            {e.latitude.toFixed(5)}, {e.longitude.toFixed(5)}
                          </Text>
                        </Flex>
                      )}

                      {e.notes && (
                        <Flex alignItems="flex-start" gap={1} mt={0.5}>
                          <Info size={12} color={colors.textMuted} style={{ marginTop: 2 }} />
                          <Text fontSize="xs" color={colors.text} fontStyle="italic">{e.notes}</Text>
                        </Flex>
                      )}
                    </Box>
                  </Flex>
                )
              })}
            </VStack>
          </Box>

          {/* Map */}
          <Box flex={1} minW={0}>
            <Text fontSize="xs" fontWeight="700" color={colors.textMuted} textTransform="uppercase" letterSpacing="0.1em" mb={3}>
              Ubicaciones
            </Text>
            {mapPoints.length === 0 ? (
              <Flex
                h="360px" bg={colors.bgHover} borderRadius="xl"
                alignItems="center" justifyContent="center" color={colors.textMuted} fontSize="sm"
              >
                Ningun evento tiene coordenadas registradas
              </Flex>
            ) : (
              <Box h="360px" borderRadius="xl" overflow="hidden" border="1px solid" borderColor={colors.border}>
                <KMap defaultCenter={mapPoints[0]} defaultZoom={12} mapTypeId="hybrid" disableDefaultUI>
                  <FitBounds points={mapPoints} padding={30} maxZoom={15} />
                  {events.map((e, idx) => {
                    if (e.latitude == null || e.longitude == null) return null
                    const def = getShipmentState(e.shipmentState)
                    return (
                      <AdvancedMarker
                        key={e.id}
                        position={{ lat: e.latitude, lng: e.longitude }}
                        onMouseEnter={() => setHovered(idx)}
                        onMouseLeave={() => setHovered((cur) => (cur === idx ? null : cur))}
                      >
                        {createStateIcon(def.color, `${idx + 1}. ${def.label}`)}
                      </AdvancedMarker>
                    )
                  })}
                  {hovered != null && events[hovered]?.latitude != null && events[hovered]?.longitude != null && (
                    <InfoWindow
                      position={{ lat: events[hovered].latitude as number, lng: events[hovered].longitude as number }}
                      pixelOffset={[0, -24]}
                      disableAutoPan
                      headerDisabled
                    >
                      <Box fontFamily="'Instrument Sans', sans-serif" p={1}>
                        <Text fontSize="xs" fontWeight="bold" color="#1a1a1a">
                          {getShipmentState(events[hovered].shipmentState).label}
                        </Text>
                        <Text fontSize="11px" color="#555">{formatDateTime(events[hovered].changedAt)}</Text>
                        <Text fontSize="10px" color="#888">{events[hovered].changedByUserName ?? '-'}</Text>
                      </Box>
                    </InfoWindow>
                  )}
                </KMap>
              </Box>
            )}
          </Box>
        </Flex>
      )}
    </Modal>
  )
}
